import { datadogRum } from '@datadog/browser-rum';
import { useCallback } from 'react';

import { RecordMountActionParams, useDatadog } from '@context/Datadog';

export const DD = 'flight-search-result';
export const SABRE_FARE_DD = 'sabre-search-result';

const TIME_TO_FIRST_FETCH_ACTION_NAME = 'time between search submitted and first fetch';
const TIME_FROM_SEARCH_TO_FIRST_RESULT_ACTION_NAME =
  'time between search submitted and first result';
const TIME_FROM_FETCH_TO_RECEIVE_FIRST_DATA_ACTION_NAME =
  'time between fetch and first data received';
const TIME_TO_RENDER_FIRST_DATA_ACTION_NAME = 'time to render first data received';
const NUMBER_OF_FARES_IN_FIRST_POLL_ACTION_NAME = 'Number Of Fares In First Poll';
const TIME_FROM_SEARCH_TO_FIRST_SABRE_RESULT_ACTION_NAME =
  'time between search submitted and first sabre result';
const TIME_FROM_FETCH_TO_FIRST_SABRE_RESULT_ACTION_NAME =
  'time between fetch and first sabre result';

const TIME_FOR_EACH_POLL_ACTION_NAME = 'time between fetch and response reception for each poll';

const TIME_FOR_PARSING_FLIGHT_SEARCH_RESULTS_ACTION_NAME = 'time for parsing flight search results';

const SEARCH_API_LATENCY_ACTION_NAME = 'search api latency';
const SEARCH_RESPONSE_PROCESSING_TIME_ACTION_NAME = 'search response processing time';
const POLLING_LATENCY_ACTION_NAME = 'polling latency';
const POLLING_PROCESSING_TIME_ACTION_NAME = 'polling processing time';
const POLLING_PROCESSING_TIME_AND_WAITING_TIME_ACTION_NAME =
  'polling processing time and waiting time';

const FIRST_POLL_WITH_SABRE_FARE_ACTION_NAME = 'first poll with sabre fare';
const AGGREGATE_RESULT_ACTION_NAME = 'time to aggregate result';
const TRIP_VIEW_MODEL_PROCESSING_ACTION_NAME = 'time to process data into trip view model';
const FILTER_PROCESSING_ACTION_NAME = 'time to process data into filter';

type OfferEngineCustomData = {
  flight_offer_engine_integration_ab_test_variant?: string;
};

type PollingCustomData = {
  index: number;
  numberOfFares?: number;
};

type TripViewModelProcessingCustomData = {
  index: number;
  name: string;
  numberOfFares?: number;
  numberOfTrips?: number;
  numberOfLegs?: number;
  numberOfTripViewModels?: number;
};

type FilterProcessingCustomData = {
  index: number;
  name: string;
};

const getRecordKey = (actionName: string) => `${DD}:${actionName}`;

const useFlightSearchResultsDatadog = () => {
  const {
    startRecordingAction,
    stopRecordingAction,
    recordMountAction,
    recordChangeAction,
    abortRecording,
  } = useDatadog();

  const abortSabreFareMountRecording = useCallback(() => {
    abortRecording(`${SABRE_FARE_DD}:${TIME_FROM_SEARCH_TO_FIRST_SABRE_RESULT_ACTION_NAME}`);
  }, [abortRecording]);

  const recordChangeFilterAction = useCallback(() => {
    recordChangeAction({ dd: DD, type: 'filter' });
    abortSabreFareMountRecording();
  }, [recordChangeAction, abortSabreFareMountRecording]);

  const recordChangeSortAction = useCallback(() => {
    recordChangeAction({ dd: DD, type: 'sort' });
    abortSabreFareMountRecording();
  }, [recordChangeAction]);

  const recordTimeToFirstResultAction = useCallback(() => {
    recordMountAction({
      dd: DD,
      actionName: TIME_FROM_SEARCH_TO_FIRST_RESULT_ACTION_NAME,
      customDataFromAttributeMap: {
        flight_offer_engine_integration_ab_test_variant:
          'data-dd-offer-engine-integration-ab-test-variant',
      },
    });
  }, [recordMountAction]);

  const startRecordingTimeToFirstFetchAction = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(TIME_TO_FIRST_FETCH_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingTimeToFirstFetchAction = useCallback(() => {
    stopRecordingAction({
      actionName: TIME_TO_FIRST_FETCH_ACTION_NAME,
      recordKey: getRecordKey(TIME_TO_FIRST_FETCH_ACTION_NAME),
    });
  }, [stopRecordingAction]);

  const startRecordingFetchTimeUntilFirstDataAction = useCallback(() => {
    startRecordingAction({
      recordKey: getRecordKey(TIME_FROM_FETCH_TO_RECEIVE_FIRST_DATA_ACTION_NAME),
    });
  }, [startRecordingAction]);

  const stopRecordingFetchTimeUntilFirstDataAction = useCallback(
    (customData: OfferEngineCustomData) => {
      stopRecordingAction({
        actionName: TIME_FROM_FETCH_TO_RECEIVE_FIRST_DATA_ACTION_NAME,
        recordKey: getRecordKey(TIME_FROM_FETCH_TO_RECEIVE_FIRST_DATA_ACTION_NAME),
        customData,
      });
    },
    [stopRecordingAction],
  );

  const recordFirstPollWithSabreFareAction = useCallback(
    (customData: PollingCustomData) => {
      recordMountAction({
        dd: SABRE_FARE_DD,
        actionName: FIRST_POLL_WITH_SABRE_FARE_ACTION_NAME,
        customData,
      });
    },
    [recordMountAction],
  );

  const recordRenderTimeFromFirstDataAction = useCallback(
    (params: Pick<RecordMountActionParams, 'onStop' | 'suppressAction'>) => {
      recordMountAction({ dd: DD, actionName: TIME_TO_RENDER_FIRST_DATA_ACTION_NAME, ...params });
    },
    [recordMountAction],
  );

  const recordNumberOfFaresInFirstPoll = useCallback((numberOfFares: number) => {
    datadogRum.addAction(NUMBER_OF_FARES_IN_FIRST_POLL_ACTION_NAME, {
      count: numberOfFares,
    });
  }, []);

  const recordTimeFromSearchToFirstSabreFareResult = useCallback(() => {
    recordMountAction({
      dd: SABRE_FARE_DD,
      actionName: TIME_FROM_SEARCH_TO_FIRST_SABRE_RESULT_ACTION_NAME,
      customDataFromAttributeMap: {
        flight_offer_engine_integration_ab_test_variant:
          'data-dd-offer-engine-integration-ab-test-variant',
      },
    });
  }, []);

  const startRecordingFetchTimeUntilFirstSabreFareResult = useCallback(() => {
    startRecordingAction({
      recordKey: getRecordKey(TIME_FROM_FETCH_TO_FIRST_SABRE_RESULT_ACTION_NAME),
    });
  }, [startRecordingAction]);

  const stopRecordingFetchTimeUntilFirstSabreFareResult = useCallback(
    (customData: OfferEngineCustomData) => {
      stopRecordingAction({
        actionName: TIME_FROM_FETCH_TO_FIRST_SABRE_RESULT_ACTION_NAME,
        recordKey: getRecordKey(TIME_FROM_FETCH_TO_FIRST_SABRE_RESULT_ACTION_NAME),
        customData,
      });
    },
    [stopRecordingAction],
  );

  const abortFlightSearchResultRecordings = () => {
    abortRecording(getRecordKey(TIME_TO_FIRST_FETCH_ACTION_NAME));
    abortRecording(getRecordKey(TIME_FROM_FETCH_TO_RECEIVE_FIRST_DATA_ACTION_NAME));
    abortRecording(getRecordKey(TIME_TO_RENDER_FIRST_DATA_ACTION_NAME));
    abortRecording(getRecordKey(TIME_FROM_SEARCH_TO_FIRST_RESULT_ACTION_NAME));
    abortRecording(getRecordKey(TIME_FROM_FETCH_TO_FIRST_SABRE_RESULT_ACTION_NAME));
    abortRecording(getRecordKey(SEARCH_API_LATENCY_ACTION_NAME));
    abortRecording(getRecordKey(SEARCH_RESPONSE_PROCESSING_TIME_ACTION_NAME));
    abortRecording(getRecordKey(POLLING_LATENCY_ACTION_NAME));
    abortRecording(getRecordKey(POLLING_PROCESSING_TIME_ACTION_NAME));
    abortRecording(getRecordKey(TIME_FOR_EACH_POLL_ACTION_NAME));
    abortRecording(getRecordKey(POLLING_PROCESSING_TIME_AND_WAITING_TIME_ACTION_NAME));
    abortRecording(getRecordKey(AGGREGATE_RESULT_ACTION_NAME));
    abortRecording(getRecordKey(TRIP_VIEW_MODEL_PROCESSING_ACTION_NAME));
    abortRecording(getRecordKey(FILTER_PROCESSING_ACTION_NAME));
    abortSabreFareMountRecording();
  };

  const startRecordingTimeForEachPoll = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(TIME_FOR_EACH_POLL_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingTimeForEachPoll = useCallback(
    (numberOfFares: number) => {
      stopRecordingAction({
        actionName: TIME_FOR_EACH_POLL_ACTION_NAME,
        recordKey: getRecordKey(TIME_FOR_EACH_POLL_ACTION_NAME),
        customData: {
          number_of_fares: numberOfFares,
        },
      });
    },
    [stopRecordingAction],
  );

  const recordTimeForParsingFlightSearchResults = useCallback(
    (time: number, numberOfFares: number) => {
      datadogRum.addAction(TIME_FOR_PARSING_FLIGHT_SEARCH_RESULTS_ACTION_NAME, {
        value: time,
        number_of_fares: numberOfFares,
      });
    },
    [],
  );

  const startRecordingSearchApiLatency = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(SEARCH_API_LATENCY_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingSearchApiLatency = useCallback(() => {
    stopRecordingAction({
      actionName: SEARCH_API_LATENCY_ACTION_NAME,
      recordKey: getRecordKey(SEARCH_API_LATENCY_ACTION_NAME),
    });
  }, [stopRecordingAction]);

  const startRecordingSearchResponseProcessingTime = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(SEARCH_RESPONSE_PROCESSING_TIME_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingSearchResponseProcessingTime = useCallback(
    (customData?: Record<string, unknown>) => {
      stopRecordingAction({
        actionName: SEARCH_RESPONSE_PROCESSING_TIME_ACTION_NAME,
        recordKey: getRecordKey(SEARCH_RESPONSE_PROCESSING_TIME_ACTION_NAME),
        customData,
      });
    },
    [stopRecordingAction],
  );

  const startRecordingPollingLatency = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(POLLING_LATENCY_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingPollingLatency = useCallback(
    (customData: PollingCustomData) => {
      stopRecordingAction({
        actionName: POLLING_LATENCY_ACTION_NAME,
        recordKey: getRecordKey(POLLING_LATENCY_ACTION_NAME),
        customData,
      });
    },
    [stopRecordingAction],
  );

  const startRecordingPollingProcessingTime = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(POLLING_PROCESSING_TIME_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingPollingProcessingTime = useCallback(
    (customData: PollingCustomData) => {
      stopRecordingAction({
        actionName: POLLING_PROCESSING_TIME_ACTION_NAME,
        recordKey: getRecordKey(POLLING_PROCESSING_TIME_ACTION_NAME),
        customData,
      });
    },
    [stopRecordingAction],
  );
  const startRecordingPollingProcessingTimeAndWaitingTime = useCallback(() => {
    startRecordingAction({
      recordKey: getRecordKey(POLLING_PROCESSING_TIME_AND_WAITING_TIME_ACTION_NAME),
    });
  }, [startRecordingAction]);

  const stopRecordingPollingProcessingTimeAndWaitingTime = useCallback(
    (customData: PollingCustomData) => {
      stopRecordingAction({
        actionName: POLLING_PROCESSING_TIME_AND_WAITING_TIME_ACTION_NAME,
        recordKey: getRecordKey(POLLING_PROCESSING_TIME_AND_WAITING_TIME_ACTION_NAME),
        customData,
      });
    },
    [stopRecordingAction],
  );

  const startRecordingAggregateResult = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(AGGREGATE_RESULT_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingAggregateResult = useCallback(() => {
    stopRecordingAction({
      actionName: AGGREGATE_RESULT_ACTION_NAME,
      recordKey: getRecordKey(AGGREGATE_RESULT_ACTION_NAME),
    });
  }, [stopRecordingAction]);

  const startRecordingTripViewModelProcessing = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(TRIP_VIEW_MODEL_PROCESSING_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingTripViewModelProcessing = useCallback(
    (customData: TripViewModelProcessingCustomData) => {
      stopRecordingAction({
        actionName: TRIP_VIEW_MODEL_PROCESSING_ACTION_NAME,
        recordKey: getRecordKey(TRIP_VIEW_MODEL_PROCESSING_ACTION_NAME),
        customData,
      });
    },
    [stopRecordingAction],
  );

  const startRecordingFilterProcessing = useCallback(() => {
    startRecordingAction({ recordKey: getRecordKey(FILTER_PROCESSING_ACTION_NAME) });
  }, [startRecordingAction]);

  const stopRecordingFilterProcessing = useCallback(
    (customData: FilterProcessingCustomData) => {
      stopRecordingAction({
        actionName: FILTER_PROCESSING_ACTION_NAME,
        recordKey: getRecordKey(FILTER_PROCESSING_ACTION_NAME),
        customData,
      });
    },
    [stopRecordingAction],
  );

  return {
    recordChangeFilterAction,
    recordChangeSortAction,
    recordTimeToFirstResultAction,
    recordTimeFromSearchToFirstSabreFareResult,

    startRecordingTimeToFirstFetchAction,
    startRecordingFetchTimeUntilFirstDataAction,

    stopRecordingTimeToFirstFetchAction,
    stopRecordingFetchTimeUntilFirstDataAction,
    recordRenderTimeFromFirstDataAction,

    recordNumberOfFaresInFirstPoll,

    abortFlightSearchResultRecordings,
    startRecordingFetchTimeUntilFirstSabreFareResult,
    stopRecordingFetchTimeUntilFirstSabreFareResult,

    startRecordingTimeForEachPoll,
    stopRecordingTimeForEachPoll,

    recordTimeForParsingFlightSearchResults,

    startRecordingSearchApiLatency,
    stopRecordingSearchApiLatency,
    startRecordingSearchResponseProcessingTime,
    stopRecordingSearchResponseProcessingTime,
    startRecordingPollingLatency,
    stopRecordingPollingLatency,
    startRecordingPollingProcessingTime,
    stopRecordingPollingProcessingTime,

    recordFirstPollWithSabreFareAction,

    startRecordingPollingProcessingTimeAndWaitingTime,
    stopRecordingPollingProcessingTimeAndWaitingTime,

    startRecordingAggregateResult,
    stopRecordingAggregateResult,

    startRecordingTripViewModelProcessing,
    stopRecordingTripViewModelProcessing,

    startRecordingFilterProcessing,
    stopRecordingFilterProcessing,
  };
};

export default useFlightSearchResultsDatadog;
